
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Paperclip, Trash2, Download, Video, Image, FileText, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Task } from '@/types';
import { Loader } from '../Loader';
import { fileAPI } from '@/api/fileAPI';
import { FileMeta } from '@/types/files';
import { MAX_UPLOAD_SIZE_BYTES, MAX_UPLOAD_SIZE_MB } from '@/utils/constants';
import { useClipboardFilePaste } from '@/hooks/useClipboardFilePaste';
import { FilePreviewModal } from '../FilePreviewModal';
import { FileCard } from '../FileCard';
import { RichTextEditor, isRichTextEmpty } from '@/components/RichTextEditor/RichTextEditor';
import { ensureHtml, sanitizeHtml, processHtmlForDisplay } from '@/utils/richText';

interface TaskDescriptionProps {
  task: Task;
  onDescriptionUpdate: (description: string) => Promise<void>;
  disabled?: boolean;
}

export const TaskDescription: React.FC<TaskDescriptionProps> = ({
  task,
  disabled = false,
  onDescriptionUpdate,
}) => {
  const initialDescription = useMemo(() => ensureHtml(task.description || ''), [task.description]);
  const [isEditing, setIsEditing] = useState(false);
  const [description, setDescription] = useState(initialDescription);
  const [isLoading, setIsLoading] = useState(false);
  const [attachments, setAttachments] = useState<FileMeta[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<FileMeta | null>(null);
  const [previewFile, setPreviewFile] = useState<FileMeta | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch files
  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const all = await fileAPI.getFiles(task.id);
        setAttachments(all.filter((f) => f.source === 'description'));
      } catch (err) {
        console.error('Failed to load attachments:', err);
      }
    };
    if (task.id) fetchFiles();
  }, [task.id]);

  useEffect(() => {
    if (!isEditing) {
      setDescription(initialDescription);
    }
  }, [initialDescription, isEditing]);

  const processFiles = useCallback(
    async (list: File[]) => {
      if (!list.length) return;

      const oversized = list.filter((file) => file.size > MAX_UPLOAD_SIZE_BYTES);
      const withinLimit = list.filter((file) => file.size <= MAX_UPLOAD_SIZE_BYTES);

      if (oversized.length) {
        const first = oversized[0];
        let message = `'${first.name}' is ${(first.size / (1024 * 1024)).toFixed(2)}MB. Maximum allowed size is ${MAX_UPLOAD_SIZE_MB}MB.`;
        if (oversized.length > 1) {
          message += ` ${oversized.length - 1} additional file(s) exceed the limit.`;
        }
        toast.error(message);
      }

      if (withinLimit.length === 0) {
        return;
      }

      setIsUploading(true);
      try {
        const uploaded = await fileAPI.uploadFiles(withinLimit, task.id, 'description');
        setAttachments((prev) => [...prev, ...uploaded]);
        toast.success('Files uploaded successfully!');
      } catch (err) {
        console.error('Upload failed:', err);
        toast.error('Failed to upload files');
      } finally {
        setIsUploading(false);
      }
    },
    [task.id],
  );

  // Upload files
  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;

    const list = Array.from(files);
    const oversized = list.filter((file) => file.size > MAX_UPLOAD_SIZE_BYTES);
    const withinLimit = list.filter((file) => file.size <= MAX_UPLOAD_SIZE_BYTES);

    if (oversized.length) {
      const first = oversized[0];
      let message = `'${first.name}' is ${(first.size / (1024 * 1024)).toFixed(2)}MB. Maximum allowed size is ${MAX_UPLOAD_SIZE_MB}MB.`;
      if (oversized.length > 1) {
        message += ` ${oversized.length - 1} additional file(s) exceed the limit.`;
      }
      toast.error(message);
    }

    if (withinLimit.length === 0) {
      e.target.value = '';
      return;
    }

    await processFiles(withinLimit);
    e.target.value = '';
  };

  const handlePaste = useClipboardFilePaste({
    contextLabel: 'description-attachment',
    onFiles: async (files) => {
      await processFiles(files);
    },
  });

  const handleSave = async () => {
    const sanitized = sanitizeHtml(description);
    const existing = sanitizeHtml(initialDescription);
    if (sanitized !== existing) {
      setIsLoading(true);
      try {
        await onDescriptionUpdate(sanitized);
        setIsEditing(false);
      } catch (error) {
        console.error('Failed to update description:', error);
      } finally {
        setIsLoading(false);
      }
    } else setIsEditing(false);
  };

  const handleCancel = () => {
    setDescription(initialDescription);
    setIsEditing(false);
  };


  return (
    <div className="mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-900">Description</h3>

        {/* 📎 Paperclip Button */}
        {/* <div className="flex items-center gap-2">
          <input
            type="file"
            multiple
            ref={fileInputRef}
            onChange={handleUpload}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="flex items-center gap-1 text-sm text-gray-600 hover:text-blue-600 disabled:opacity-50"
          >
            {isUploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Uploading...
              </>
            ) : (
              <>
                <Paperclip className="w-4 h-4" /> Attach
              </>
            )}
          </button>
        </div> */}
      </div>

      {/* Description Box */}
      <div className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
        {isEditing ? (
          <>
            <div onPaste={handlePaste}>
                  <RichTextEditor
                value={description}
                onChange={(html) => setDescription(html)}
                placeholder="Add a description..."
                toolbarAccessory={(
                  <>
                    <input
                      type="file"
                      multiple
                      ref={fileInputRef}
                      onChange={handleUpload}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      className="flex items-center gap-1 text-sm text-gray-600 hover:text-blue-600 disabled:opacity-50"
                    >
                      {isUploading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" /> Uploading...
                        </>
                      ) : (
                        <>
                          <Paperclip className="w-4 h-4" /> Attach
                        </>
                      )}
                    </button>
                  </>
                )}
              />

            </div>
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleSave}
                disabled={isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading ? (
                  <Loader variant="overlay" size="xl" text="Saving..." />
                ) : (
                  'Save'
                )}
              </button>
              <button
                onClick={handleCancel}
                disabled={isLoading}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
            <div
              className="prose prose-sm max-w-none text-gray-700 min-h-[60px] cursor-text rich-text-view"
              onClick={() => {
              if (disabled) return; 
              setIsEditing(true)
            }}
          >
            {isRichTextEmpty(description) ? (
              <p className="text-gray-500 italic">Add a description...</p>
            ) : (
              <div dangerouslySetInnerHTML={{ __html: processHtmlForDisplay(description) }} />
            )}
          </div>
        )}
      </div>

      {/* Uploaded Files - Card Format */}
      {attachments.length > 0 && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {attachments.map((file) => (
            <FileCard
              key={file._id}
              file={file}
              onPreview={() => setPreviewFile(file)}
              onDownload={() => fileAPI.downloadFile(file._id, file.file_name)}
              onDelete={() => setFileToDelete(file)}
            />
          ))}
        </div>
      )}


      {/* File Preview Modal */}
      <FilePreviewModal
        isOpen={!!previewFile}
        onClose={() => setPreviewFile(null)}
        file={previewFile}
      />

      {/* Delete Confirmation Popup */}
      {fileToDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-80 text-center">
            <h3 className="font-semibold text-gray-800 mb-2">Delete Attachment?</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete{' '}
              <span className="font-medium text-gray-800">{fileToDelete.file_name}</span>?
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setFileToDelete(null)}
                className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    await fileAPI.deleteFile(fileToDelete._id);
                    setAttachments((prev) =>
                      prev.filter((f) => f._id !== fileToDelete._id)
                    );
                  } catch {
                    console.error('Delete failed');
                  } finally {
                    setFileToDelete(null);
                  }
                }}
                className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
